import {
  Stack,
  Typography,
  FormControl,
  TextField,
  FormHelperText,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { type Control, Controller } from 'react-hook-form';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AirIcon from '@mui/icons-material/Air';

import { type EditAuditFormBackgroundData } from '#apps/client-web/pages/audit-form-background/types';
import { getFormErrorAttributes } from '#apps/client-web/app/helpers/react-hook-form';
import { BACKGROUND_FIELD_DESCRIPTIONS } from '#apps/client-web/features/audit-form-background/constants';
import { useFormDescriptions } from '#apps/client-web/features/settings/hooks/use-form-descriptions';

export type SpaceVentilationFieldsProps = {
  control: Control<EditAuditFormBackgroundData>;
  disableForm: boolean;
};

export const VentilationFields = ({
  control,
  disableForm,
}: SpaceVentilationFieldsProps) => {
  const getDescription = useFormDescriptions(BACKGROUND_FIELD_DESCRIPTIONS);

  return (
    <Accordion variant="outlined">
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="panel1-content"
        id="panel1-header">
        <Stack direction="row" alignItems="center" spacing={1}>
          <AirIcon fontSize="small" />
          <Typography variant="body1">Ventilation</Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={3}>
          <Controller
            control={control}
            name="ventilation.system"
            disabled={disableForm}
            rules={{ maxLength: 120 }}
            render={({ field, fieldState }) => (
              <FormControl
                fullWidth
                {...getFormErrorAttributes(fieldState.error)}>
                <TextField
                  {...field}
                  label="System"
                  type="text"
                  multiline
                  rows={2}
                  variant="outlined"
                  {...getFormErrorAttributes(fieldState.error)}
                />
                <FormHelperText>{fieldState.error?.message ?? getDescription(field.name)}</FormHelperText>
              </FormControl>
            )}
          />
          <Controller
            control={control}
            name="ventilation.isVariableAirVolumeSystem"
            disabled={disableForm}
            render={({ field, fieldState }) => (
              <FormControl
                fullWidth
                {...getFormErrorAttributes(fieldState.error)}>
                <FormControlLabel
                  control={<Switch {...field} checked={field.value || false} />}
                  label="Variable air volume system?"
                  labelPlacement="start"
                  sx={{
                    justifyContent: 'space-between',
                    ml: 0,
                  }}
                />
                <FormHelperText>{fieldState.error?.message ?? getDescription(field.name)}</FormHelperText>
              </FormControl>
            )}
          />
          <Controller
            control={control}
            name="ventilation.hasCO2Sensors"
            disabled={disableForm}
            render={({ field, fieldState }) => (
              <FormControl
                fullWidth
                {...getFormErrorAttributes(fieldState.error)}>
                <FormControlLabel
                  control={<Switch {...field} checked={field.value || false} />}
                  label="Has CO2 sensors?"
                  labelPlacement="start"
                  sx={{
                    justifyContent: 'space-between',
                    ml: 0,
                  }}
                />
                <FormHelperText>{fieldState.error?.message ?? getDescription(field.name)}</FormHelperText>
              </FormControl>
            )}
          />
          <Controller
            control={control}
            name="ventilation.hasParkadeCOSensors"
            disabled={disableForm}
            render={({ field, fieldState }) => (
              <FormControl
                fullWidth
                {...getFormErrorAttributes(fieldState.error)}>
                <FormControlLabel
                  control={<Switch {...field} checked={field.value || false} />}
                  label="Has parkade CO sensors?"
                  labelPlacement="start"
                  sx={{
                    justifyContent: 'space-between',
                    ml: 0,
                  }}
                />
                <FormHelperText>{fieldState.error?.message ?? getDescription(field.name)}</FormHelperText>
              </FormControl>
            )}
          />
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};
